/**
 * Utilities for socialLinks[] (manual list) and platform derivation.
 * Handles both array form (SocialLink[]) and legacy object form for backward compatibility.
 */

import type { SocialLink, SocialPlatform } from '../types';

const PLATFORM_ORDER: SocialPlatform[] = [
  'tiktok',
  'instagram',
  'youtube',
  'twitter',
  'linkedin',
  'website',
  'other',
];

/** Normalize handle: trim @, lowercase for IG/TikTok. */
export function normalizeSocialHandle(
  platform: SocialPlatform,
  handle: string | null | undefined
): string {
  if (handle == null || typeof handle !== 'string') return '';
  const t = handle.trim().replace(/^@/, '').toLowerCase();
  if (platform === 'instagram' || platform === 'tiktok') return t;
  return handle.trim().replace(/^@/, '');
}

/** Best-effort platform from URL. */
export function platformFromUrl(url: string | null | undefined): SocialPlatform | null {
  if (!url || typeof url !== 'string') return null;
  const lower = url.toLowerCase();
  if (lower.includes('tiktok.com')) return 'tiktok';
  if (lower.includes('instagram.com') || lower.includes('instagr.am')) return 'instagram';
  if (lower.includes('youtube.com') || lower.includes('youtu.be')) return 'youtube';
  if (lower.includes('twitter.com') || lower.includes('x.com')) return 'twitter';
  if (lower.includes('linkedin.com')) return 'linkedin';
  return 'website';
}

/** Dedupe by platform; keep first occurrence. Optionally merge by platform+handle/url. */
export function dedupeSocialLinks(links: SocialLink[]): SocialLink[] {
  if (!Array.isArray(links)) return [];
  const seen = new Set<string>();
  const result: SocialLink[] = [];
  for (const link of links) {
    if (!link?.platform || !link?.url) continue;
    const key = `${link.platform}:${(link.handle ?? link.url).toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(link);
  }
  return result;
}

/** Normalize profile.socialLinks (array or legacy object) to SocialLink[]. */
export function socialLinksToArray(
  raw: unknown,
  options?: { dedupe: boolean }
): SocialLink[] {
  if (Array.isArray(raw)) {
    const arr = raw
      .filter((entry): entry is SocialLink => entry != null && typeof entry === 'object' && typeof (entry as SocialLink).platform === 'string' && typeof (entry as SocialLink).url === 'string')
      .map((entry) => ({
        platform: (entry as SocialLink).platform as SocialPlatform,
        handle: (entry as SocialLink).handle,
        url: (entry as SocialLink).url,
        addedAt: (entry as SocialLink).addedAt,
      }));
    return options?.dedupe !== false ? dedupeSocialLinks(arr) : arr;
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    const arr: SocialLink[] = [];
    for (const [key, value] of Object.entries(obj)) {
      const url = typeof value === 'string' ? value : (value && typeof value === 'object' && typeof (value as any).url === 'string' ? (value as any).url : null);
      if (!url) continue;
      const platform = (key.toLowerCase() as SocialPlatform) || platformFromUrl(url) || 'other';
      arr.push({
        platform: PLATFORM_ORDER.includes(platform) ? platform : 'other',
        url,
        handle: typeof (value as any)?.handle === 'string' ? (value as any).handle : undefined,
        addedAt: typeof (value as any)?.addedAt === 'number' ? (value as any).addedAt : undefined,
      });
    }
    return options?.dedupe !== false ? dedupeSocialLinks(arr) : arr;
  }
  return [];
}

/**
 * Get deduped, ordered platform list for Platform Performance (and chips).
 * Only includes platforms that have a link in the identity section (socialLinks).
 * When a user removes a social from identity, that platform is no longer shown here.
 */
export function getPlatformsFromProfile(profile: {
  socialLinks?: unknown;
  socialAccounts?: { tiktok?: unknown; instagram?: unknown; [k: string]: unknown };
}): SocialPlatform[] {
  const links = socialLinksToArray(profile?.socialLinks, { dedupe: true });
  const fromLinks = links.map((l) => l.platform);
  return PLATFORM_ORDER.filter((p) => fromLinks.includes(p));
}
